import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { validateAndFormatTime } from "@/lib/utils/validateAndFormatTime";
import { timeRegex } from "@/schemas/commonFields/time";
import { Clock } from "lucide-react";
import { ChangeEventHandler, forwardRef, useCallback } from "react";
import {
  ControllerRenderProps,
  FieldValues,
  useController,
} from "react-hook-form";
import { useTranslation } from "react-i18next";

interface TimeInputProps extends ControllerRenderProps<FieldValues, string> {
  disabled?: boolean;
  className?: string;
  wrapperClassName?: string;
}

export default forwardRef<HTMLInputElement, TimeInputProps>(function TimeInput(
  { disabled, className, onChange, wrapperClassName, ...props },
  ref,
) {
  const { t } = useTranslation();
  const controller = useController({ name: props.name as string });
  const onInputChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      onChange(validateAndFormatTime(e.target.value));
    },
    [onChange],
  );

  const errorStyle = "border-red-500 text-red-800 focus-visible:ring-red-500";
  const defaultStyle = "h-full rounded-sm focus-visible:ring-offset-0 pr-8";

  return (
    <div className={cn("relative", wrapperClassName)}>
      <Input
        {...props}
        ref={ref}
        disabled={disabled}
        className={cn(defaultStyle, className, {
          [errorStyle]: controller.fieldState.error,
        })}
        placeholder={t("timeInput.placeholder")}
        pattern={timeRegex.source}
        maxLength={8}
        onChange={onInputChange}
      />
      <Clock className="absolute right-3 top-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
    </div>
  );
});
